import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePassword, useMe } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert, Button, PasswordField, PasswordRules, isPasswordValid } from '../components/ui.js';
import { AuthShell } from './LoginPage.js';

/**
 * The change-password form: current password, a new one (with the live rules),
 * and a confirmation that must match. Reused by the forced-change screen and the
 * settings area.
 */
export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
  const change = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saved, setSaved] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const ready = Boolean(currentPassword) && isPasswordValid(newPassword) && newPassword === confirm;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!ready) return;
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setSaved(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirm('');
          onDone?.();
        },
      },
    );
  };

  const error =
    change.error instanceof ApiError && change.error.code === 'wrong_current_password'
      ? 'Your current password is incorrect.'
      : change.isError
        ? 'Something went wrong. Please try again.'
        : null;

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      {error ? <Alert>{error}</Alert> : null}
      {saved ? <Alert kind="info">Your password has been changed.</Alert> : null}
      <PasswordField
        label="Current password"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      <PasswordField
        label="New password"
        autoComplete="new-password"
        required
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <PasswordRules value={newPassword} />
      <PasswordField
        label="Confirm new password"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={mismatch ? 'The passwords do not match.' : undefined}
      />
      <Button type="submit" disabled={change.isPending || !ready}>
        {change.isPending ? 'Saving…' : 'Save new password'}
      </Button>
    </form>
  );
}

/**
 * Forced when `mustChangePassword` is set (the seeded admin's first login, per
 * ADR-009). Also reachable voluntarily. On success the server re-issues the
 * session, so the user stays signed in.
 */
export function ChangePasswordPage() {
  const { data: user } = useMe();
  const navigate = useNavigate();
  const forced = user?.mustChangePassword ?? false;

  return (
    <AuthShell title="Choose a new password">
      {forced ? (
        <div className="mb-4">
          <Alert kind="info">
            Set a new password before continuing — the account still has its initial one.
          </Alert>
        </div>
      ) : null}
      <ChangePasswordForm onDone={() => void navigate('/', { replace: true })} />
    </AuthShell>
  );
}
